import { useCallback, useState } from 'react'

import { Button, Input, Modal, Trans } from '@/components'

import { IFile, validFileName } from './fs'
import * as fs from './fs'

export interface RenameDialogProps {
  onCancel: () => void
  onOk: () => void
  file: IFile | undefined
  files: IFile[] | undefined
}

const RenameDialog = ({
  file,
  files,
  onCancel,
  onOk,
  domain,
}: RenameDialogProps & { domain: number }) => {
  const [loading, setLoading] = useState(false)
  const [inputValue, setInputValue] = useState<string>(file?.name || '')

  const isNameOK = useCallback(
    (name: string) => {
      if (files?.some((it: IFile) => it.name == name) || !validFileName(name)) {
        return false
      }
      return true
    },
    [files],
  )

  const _onConfirm = useCallback(() => {
    if (isNameOK(inputValue) && file) {
      const newName = inputValue
      const path = file.path,
        target = file.isDir ? `${file.dirname}${newName}/` : `${file.dirname}${newName}`
      setLoading(true)
      fs.mv({ path, target, domain })
        .then(onOk)
        .catch(() => {})
        .finally(() => {
          setLoading(false)
        })
    }
  }, [file, inputValue, isNameOK, onOk, domain])

  return (
    <Modal open={Boolean(file)} onClose={onCancel}>
      <Modal.Header>Rename</Modal.Header>
      <Modal.Body>
        <Input
          maxLength={30}
          value={file?.name}
          onChange={(event) => setInputValue(event.target.value)}
          placeholder="Please enter file name"
          onClear={() => {
            setInputValue('')
          }}
          disabled={loading}
          onPressEnter={_onConfirm}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onCancel}>
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button type="secondary" onClick={_onConfirm} disabled={loading || !isNameOK(inputValue)}>
          {loading ? (
            <Trans i18nKey="common.confirmModalOkLoading">Processing...</Trans>
          ) : (
            <Trans i18nKey="common.confirmModalOk">Confirm</Trans>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export function useRename(domain: number) {
  return {
    RenameDialog: useCallback(
      (props: RenameDialogProps) => <RenameDialog {...props} domain={domain} />,
      [domain],
    ),
  }
}
